import { AbilityContext } from "@context/can";
import { UserContext } from "@context/user";
import { useContext } from "react";
import {
  Building2,
  FileSignature,
  FileText,
  Home,
  LayoutDashboard,
  Lock,
  // PlusCircle,
  Settings,
  BookOpen,
  ShieldCheck,
  TrendingUp,
  Users,
  Wallet
} from "lucide-react";
import { KYC_LABEL, KYC_TONE, PAYMENT_MODEL, PAYMENT_MODEL_LABEL } from "@src/utility/constants/panel/common";

// `debitNotes: false` drops the entries that only exist under managed payouts —
// a self-payout brand settles with its publishers itself, so Ops never raises a
// debit note or runs a disbursement for it. The routes bounce too, so a
// hand-typed URL can't reach them either (see router/private-route.jsx).
const getOpsNav = ({ debitNotes = true } = {}) => [
  {
    id     : "dashboard",
    title  : "Dashboard",
    icon   : Home,
    navLink: "/dashboard",
    exact  : true
  },
  {
    id     : "brands",
    title  : "Brands",
    icon   : Building2,
    navLink: "/brands",
    exact  : true
  },
  // {
  //   id     : "brands-new",
  //   title  : "Onboard brand",
  //   icon   : PlusCircle,
  //   navLink: "/brands/new"
  // },
  {
    id     : "publishers",
    title  : "Publishers",
    icon   : Users,
    navLink: "/publishers"
  },
  {
    id     : "contracts",
    title  : "Contracts",
    icon   : FileSignature,
    navLink: "/contracts"
  },
  ...(debitNotes
    ? [
        {
          id     : "debit-notes",
          title  : "Debit notes",
          icon   : FileText,
          navLink: "/debit-notes"
        },
        {
          id     : "disbursements",
          title  : "Disbursements",
          icon   : Wallet,
          navLink: "/disbursements"
        }
      ]
    : []),
  { id: "guide", title: "User guide", icon: BookOpen, navLink: "/guide" },
  { id: "settings", title: "Settings", icon: Settings, navLink: "/settings" }
];

// Mirrors the Nook Pay Hub reference's Brand workspace sidebar (src/routes/brand.tsx).
// Every link is scoped to the currently-selected brand (see the topbar switcher).
// `hasDebitNotes` is false for a self-payout brand, which never has one raised
// against it — see the brand list mapping in UserContext.
const getBrandNav = (brandId, { hasDebitNotes = true } = {}) => [
  {
    id     : "brand-dashboard",
    title  : "Dashboard",
    icon   : LayoutDashboard,
    navLink: `/brand/${brandId}/dashboard`,
    exact  : true
  },
  {
    id     : "brand-publishers",
    title  : "Publishers",
    icon   : Users,
    navLink: `/brand/${brandId}/publishers`
  },
  {
    id     : "brand-publisher-contract",
    // The page now carries the brand's own ops-issued contract alongside its
    // publishers' status, so it is no longer only about publishers.
    title  : "Contract",
    icon   : FileSignature,
    navLink: `/brand/${brandId}/publisher-contract`
  },
  // One slot, two sides of the same question: a managed-payout brand pays Nook Pay
  // against a debit note, a self-payout brand pays its publishers' invoices itself.
  ...(hasDebitNotes
    ? [
        {
          id     : "brand-debit-notes",
          title  : "Debit notes",
          icon   : FileText,
          navLink: `/brand/${brandId}/debit-note`
        }
      ]
    : [
        {
          id     : "brand-disbursements",
          title  : "Disbursements",
          icon   : Wallet,
          navLink: `/brand/${brandId}/disbursements`
        }
      ]),
  // "Configure SMTP" is hidden from the nav — the route and ability remain, so
  // the page stays reachable by direct URL.
  { id: "brand-guide", title: "User guide", icon: BookOpen, navLink: `/brand/${brandId}/guide` }
];

// A brand that hasn't chosen how it pays publishers gets the setup gate as its
// only destination — that's what locks the workspace, while leaving the topbar
// (and its brand switcher) usable so the session can move to another brand.
const getBrandSetupNav = (brandId) => [
  {
    id     : "brand-setup",
    title  : "Set up workspace",
    icon   : Lock,
    navLink: `/brand/${brandId}/setup`,
    exact  : true
  }
];

// Mirrors the Nook Pay Hub reference's Publisher workspace sidebar (src/routes/publisher.tsx).
// Every link is scoped to the currently-selected publisher account (see the topbar switcher).
const getPublisherNav = (publisherId) => [
  {
    id     : "publisher-overview",
    title  : "Overview",
    icon   : LayoutDashboard,
    navLink: `/publisher/${publisherId}/overview`,
    exact  : true
  },
  {
    id     : "publisher-earnings",
    title  : "Earnings",
    icon   : TrendingUp,
    navLink: `/publisher/${publisherId}/earnings`
  },
  {
    id     : "publisher-contracts",
    title  : "Contracts",
    icon   : FileSignature,
    navLink: `/publisher/${publisherId}/contracts`
  },
  {
    id     : "publisher-kyc",
    title  : "KYC",
    icon   : ShieldCheck,
    navLink: `/publisher/${publisherId}/kyc`
  },
  { id: "publisher-guide", title: "User guide", icon: BookOpen, navLink: `/publisher/${publisherId}/guide` }
];

// ** Merge & Export
const useNavigation = () => {
  const ability = useContext(AbilityContext);
  const { userData, brands, activeBrandId, publishers, activePublisherId, opsPaymentModel } = useContext(UserContext);
  const role = userData?.user?.role;

  if (role === "ops") {
    return {
      panel: getOpsNav({ debitNotes: opsPaymentModel !== PAYMENT_MODEL.SELF_PAYOUT }),
      label: "Ops workspace"
    };
  }

  if (role === "brand") {
    const activeBrand = brands.find((brand) => brand.id === activeBrandId);
    const buildNav = activeBrand?.setupPending ? getBrandSetupNav : getBrandNav;
    return {
      panel  : activeBrandId ? buildNav(activeBrandId, activeBrand) : [],
      label  : "Brand workspace",
      context: activeBrand && {
        name             : activeBrand.name,
        kycTone          : KYC_TONE[activeBrand.kycStatus] || "neutral",
        kycLabel         : KYC_LABEL[activeBrand.kycStatus] || activeBrand.kycStatus,
        paymentModelLabel: PAYMENT_MODEL_LABEL[activeBrand.paymentModel] || activeBrand.paymentModel
      }
    };
  }

  if (role === "publisher") {
    const activePublisher = publishers.find((publisher) => publisher.id === activePublisherId);
    // activePublisher.kycStatus comes from a fresh v1/dashboard/accounts fetch on every load;
    // userData.user.kycStatus is a login-time snapshot cached in localStorage that never
    // gets invalidated when KYC status changes mid-session, so it goes stale — prefer the
    // live value and only fall back to the snapshot if the publisher list hasn't loaded yet.
    const kycStatus = activePublisher?.kycStatus || userData?.user.kycStatus;
    return {
      panel  : activePublisherId ? getPublisherNav(activePublisherId) : [],
      label  : "Publisher workspace",
      context: userData?.user && {
        name    : activePublisher?.holder_name,
        kycTone : KYC_TONE[kycStatus] || "neutral",
        kycLabel: KYC_LABEL[kycStatus] || kycStatus
      },
      footer: { contact: { email: "support@nookpay.com" } }
    };
  }

  // Other roles — defined later.
  return {
    panel: [
      {
        id      : "dashboard",
        title   : "Dashboard",
        icon    : Home,
        navLink : "/dashboard",
        resource: "dashboard",
        testId  : "dashboard",
        show    : ability.can("read", "dashboard")
      }
    ],
    label: "Workspace"
  };
};

export default useNavigation;
